import './Form.css';
import { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../contexts/AuthContext";

// ── Sub-components ─────────────────────────────────────────────────────────────

const StatusBanner = ({ status, onClose }) => {
    if (!status) return null;
    return (
        <div className={`bform-banner bform-banner--${status.type}`}>
            <span className="bform-banner-icon">{status.type === 'success' ? '✓' : '⚠'}</span>
            <span className="bform-banner-text">{status.text}</span>
            <button className="bform-banner-close" onClick={onClose} aria-label="Fermer">×</button>
        </div>
    );
};

const ImageUploadZone = ({ url, uploading, progress, onFile, variant, label, hint }) => {
    const inputRef = useRef(null);
    return (
        <div className="bform-upload">
            {label && <label className="bform-label">{label}</label>}
            {hint && <span className="bform-hint">{hint}</span>}
            <div
                className={`bform-upload-zone bform-upload-zone--${variant}${uploading ? ' bform-upload-zone--busy' : ''}`}
                onClick={() => !uploading && inputRef.current?.click()}
            >
                {uploading ? (
                    <div className="bform-upload-placeholder">
                        <div className="bform-upload-spinner" />
                        <span>{progress}</span>
                    </div>
                ) : url ? (
                    <>
                        <img
                            src={url}
                            alt=""
                            className={`bform-upload-img${variant === 'logo' ? ' bform-upload-img--contain' : ''}`}
                        />
                        <div className="bform-upload-hover">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="17 8 12 3 7 8"/>
                                <line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                            <span>Modifier</span>
                        </div>
                    </>
                ) : (
                    <div className="bform-upload-placeholder">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        <span>Cliquer pour importer</span>
                    </div>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="bform-upload-input"
                    onChange={onFile}
                    disabled={uploading}
                />
            </div>
        </div>
    );
};

const SectionIcon = ({ children }) => (
    <div className="bform-section-icon">{children}</div>
);

// ── Main component ─────────────────────────────────────────────────────────────

const Form = () => {
    const apiUrl = process.env.REACT_APP_API || '';
    const { me, user, setUser } = useContext(AuthContext);

    const [loading, setLoading] = useState(true);
    const [brand, setBrand] = useState(null);
    const [status, setStatus] = useState(null); // { type: 'success'|'error', text: string }

    const [creating, setCreating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [brandName, setBrandName] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [coverUrl, setCoverUrl] = useState('');
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [logoUploadProgress, setLogoUploadProgress] = useState('');
    const [uploadingCover, setUploadingCover] = useState(false);
    const [coverUploadProgress, setCoverUploadProgress] = useState('');

    const [description, setDescription] = useState('');
    const [website, setWebsite] = useState('');
    const [documents, setDocuments] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [salesCount, setSalesCount] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const u = user || await me({ silent: true });
                if (cancelled) return;
                if (!u) {
                    setStatus({ type: 'error', text: 'Utilisateur non connecté.' });
                    setLoading(false);
                    return;
                }
                const brandId = u?.brand?._id || u?.brandId || u?.brand;
                if (!brandId) {
                    setBrand(null);
                    setLoading(false);
                    return;
                }
                const res = await fetch(apiUrl + '/brand/' + brandId, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                });
                if (!res.ok) {
                    setStatus({ type: 'error', text: 'Impossible de récupérer la marque.' });
                    setLoading(false);
                    return;
                }
                const data = await res.json();
                const b = data?.brand || data || null;
                setBrand(b);
                const infos = b?.brandinfos || b?.infos || b?.brandInfos || {};
                setBrandName(b?.name || '');
                setLogoUrl(b?.logo || b?.logoUrl || '');
                setCoverUrl(b?.coverUrl || '');
                setDescription(infos?.description || '');
                setWebsite(infos?.website || '');
                setDocuments((infos?.documents || []).join(', '));
                setContactEmail(infos?.contactEmail || b?.contactEmail || '');
                setContactPhone(infos?.contactPhone || b?.contactPhone || '');
                setSalesCount(infos?.salesCount || 0);
            } catch (err) {
                console.error(err);
                setStatus({ type: 'error', text: 'Erreur réseau lors de la récupération.' });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [user, me, apiUrl]);

    const uploadImage = async (file, setUploading, setProgress, setUrl, urlKey) => {
        if (!file) return;
        setUploading(true);
        try {
            setProgress('Initialisation…');
            const initRes = await fetch(apiUrl + '/files/upload/init', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ contentType: file.type, contentLength: file.size, filename: file.name }),
            });
            if (!initRes.ok) throw new Error('Erreur init upload');
            const initData = await initRes.json();

            setProgress('Envoi…');
            const putRes = await fetch(initData.presignedUrl, {
                method: 'PUT',
                headers: { 'Content-Type': file.type },
                body: file,
            });
            if (!putRes.ok) throw new Error('Erreur envoi S3');

            setProgress('Traitement…');
            const confirmRes = await fetch(apiUrl + '/files/upload/confirm', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ uploadId: initData.uploadId, contentType: file.type }),
            });
            if (!confirmRes.ok) throw new Error('Erreur confirmation');
            const confirmData = await confirmRes.json();

            let finalUrl = null;
            for (let i = 0; i < 30; i++) {
                await new Promise(r => setTimeout(r, 1000));
                const statusRes = await fetch(apiUrl + '/files/upload/status/' + confirmData.id, { credentials: 'include' });
                const statusData = await statusRes.json();
                if (statusData.status === 'ready') {
                    finalUrl = statusData.urls?.[urlKey] || statusData.urls?.md || statusData.urls?.original;
                    break;
                }
                if (statusData.status === 'error') throw new Error('Erreur traitement wmedia');
            }
            if (!finalUrl) throw new Error('Timeout wmedia');
            setUrl(finalUrl);
        } catch (err) {
            setStatus({ type: 'error', text: 'Erreur upload : ' + err.message });
        } finally {
            setUploading(false);
            setProgress('');
        }
    };

    const handleLogoUpload = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        uploadImage(file, setUploadingLogo, setLogoUploadProgress, setLogoUrl, 'md');
    };

    const handleCoverUpload = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        uploadImage(file, setUploadingCover, setCoverUploadProgress, setCoverUrl, 'lg');
    };

    const createBrand = async (e) => {
        e.preventDefault();
        setCreating(true);
        setStatus(null);
        try {
            const res = await fetch(apiUrl + '/brand', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ name: brandName, logo: logoUrl }),
            });
            const data = await res.json();
            if (!res.ok) {
                setStatus({ type: 'error', text: data.message || 'Erreur lors de la création de la marque' });
                return;
            }
            const created = data?.brand || data;
            setBrand(created);
            try {
                const refreshed = await me({ silent: true });
                if (!refreshed && setUser) setUser(prev => prev ? { ...prev, brand: created._id || created.id || created } : prev);
            } catch {
                if (setUser) setUser(prev => prev ? { ...prev, brand: created._id || created.id || created } : prev);
            }
            setStatus({ type: 'success', text: 'Marque créée ! Complétez maintenant les informations.' });
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', text: 'Erreur réseau lors de la création.' });
        } finally {
            setCreating(false);
        }
    };

    const saveBrandInfos = async (e) => {
        e.preventDefault();
        if (!brand?._id && !brand?.id) {
            setStatus({ type: 'error', text: 'Aucune marque à mettre à jour.' });
            return;
        }
        setSaving(true);
        setStatus(null);
        const brandId = brand._id || brand.id || brand;
        const infosPayload = {
            description,
            website,
            documents: documents.split(',').map(s => s.trim()).filter(Boolean),
            contactEmail,
            contactPhone,
            salesCount: Number(salesCount) || 0,
        };
        try {
            await fetch(apiUrl + '/brand/' + brandId, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ name: brandName, logo: logoUrl, coverUrl }),
            });
            const res = await fetch(apiUrl + '/brandinfos/' + brand?.infos?._id, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(infosPayload),
            });
            const data = await res.json();
            if (!res.ok) {
                setStatus({ type: 'error', text: data.message || 'Échec lors de la sauvegarde des informations' });
                return;
            }
            setStatus({ type: 'success', text: 'Informations sauvegardées.' });
            try {
                const refreshed = await me({ silent: true });
                if (!refreshed && setUser) setUser(prev => prev ? { ...prev, brand: brand._id || brand.id || brand } : prev);
            } catch {
                if (setUser) setUser(prev => prev ? { ...prev, brand: brand._id || brand.id || brand } : prev);
            }
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', text: 'Erreur réseau lors de la sauvegarde.' });
        } finally {
            setSaving(false);
        }
    };

    // ── Loading ──────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="bform-page">
                <div className="bform-loading">
                    <div className="bform-loading-dot" />
                    <div className="bform-loading-dot" />
                    <div className="bform-loading-dot" />
                </div>
            </div>
        );
    }

    // ── Phase 1 : create brand ────────────────────────────────────────────────

    if (!brand) {
        return (
            <div className="bform-page">
                <div className="bform-create-wrap">
                    <form className="bform-create-card" onSubmit={createBrand}>
                        <div className="bform-create-header">
                            <h1 className="bform-create-title">Créer votre marque</h1>
                            <p className="bform-create-sub">Commençons par les informations essentielles.</p>
                        </div>

                        {status && (
                            <StatusBanner status={status} onClose={() => setStatus(null)} />
                        )}

                        <ImageUploadZone
                            url={logoUrl}
                            uploading={uploadingLogo}
                            progress={logoUploadProgress}
                            onFile={handleLogoUpload}
                            variant="logo"
                            label="Logo de la marque"
                            hint="Format carré recommandé (PNG ou SVG)"
                        />

                        <div className="bform-field">
                            <label className="bform-label">
                                Nom de la marque
                            </label>
                            <input
                                className="bform-input"
                                type="text"
                                value={brandName}
                                onChange={e => setBrandName(e.target.value)}
                                placeholder="Ex : Maison Lumière"
                                required
                            />
                        </div>

                        <button className="bform-btn-primary" type="submit" disabled={creating}>
                            {creating && <span className="bform-btn-save-spinner" />}
                            {creating ? 'Création en cours…' : 'Créer la marque'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // ── Phase 2 : edit brand infos ────────────────────────────────────────────

    return (
        <div className="bform-page">
            <div className="bform-header">
                <span className="bform-header-title">Informations de la marque</span>
                <span className="bform-header-brand">
                    <span className="bform-header-brandname">{brandName || brand?.name}</span>
                </span>
            </div>

            <StatusBanner status={status} onClose={() => setStatus(null)} />

            <form className="bform-body" onSubmit={saveBrandInfos} id="bform-main">

                {/* ── Section Identité visuelle ─────────────────────────── */}
                <div className="bform-section">
                    <div className="bform-section-head">
                        <SectionIcon>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="18" height="18" rx="3"/>
                                <circle cx="8.5" cy="8.5" r="1.5"/>
                                <polyline points="21 15 16 10 5 21"/>
                            </svg>
                        </SectionIcon>
                        <span className="bform-section-title">Identité visuelle</span>
                    </div>
                    <div className="bform-section-body">
                        <div className="bform-field">
                            <label className="bform-label">Nom de la marque</label>
                            <input
                                className="bform-input"
                                type="text"
                                value={brandName}
                                onChange={e => setBrandName(e.target.value)}
                                placeholder="Nom de la marque"
                            />
                        </div>
                        <div className="bform-uploads-row">
                            <ImageUploadZone
                                url={logoUrl}
                                uploading={uploadingLogo}
                                progress={logoUploadProgress}
                                onFile={handleLogoUpload}
                                variant="logo"
                                label="Logo"
                                hint="Format carré"
                            />
                            <ImageUploadZone
                                url={coverUrl}
                                uploading={uploadingCover}
                                progress={coverUploadProgress}
                                onFile={handleCoverUpload}
                                variant="cover"
                                label="Image de couverture"
                                hint="Affichée dans la recherche Wink"
                            />
                        </div>
                    </div>
                </div>

                {/* ── Section Description ───────────────────────────────── */}
                <div className="bform-section">
                    <div className="bform-section-head">
                        <SectionIcon>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                                <line x1="16" y1="13" x2="8" y2="13"/>
                                <line x1="16" y1="17" x2="8" y2="17"/>
                                <polyline points="10 9 9 9 8 9"/>
                            </svg>
                        </SectionIcon>
                        <span className="bform-section-title">Description</span>
                    </div>
                    <div className="bform-section-body">
                        <div className="bform-field">
                            <label className="bform-label">Présentation de la marque</label>
                            <textarea
                                className="bform-textarea"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="Décrivez votre marque, son univers, ses valeurs…"
                                rows={4}
                            />
                        </div>
                        <div className="bform-field">
                            <label className="bform-label">
                                Site web
                                <span className="bform-optional">Optionnel</span>
                            </label>
                            <input
                                className="bform-input"
                                type="url"
                                value={website}
                                onChange={e => setWebsite(e.target.value)}
                                placeholder="https://votremarque.com"
                            />
                        </div>
                    </div>
                </div>

                {/* ── Section Contact ───────────────────────────────────── */}
                <div className="bform-section">
                    <div className="bform-section-head">
                        <SectionIcon>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                            </svg>
                        </SectionIcon>
                        <span className="bform-section-title">Contact</span>
                    </div>
                    <div className="bform-section-body">
                        <div className="bform-row">
                            <div className="bform-field">
                                <label className="bform-label">Email de contact</label>
                                <input
                                    className="bform-input"
                                    type="email"
                                    value={contactEmail}
                                    onChange={e => setContactEmail(e.target.value)}
                                    placeholder="contact@marque.com"
                                />
                            </div>
                            <div className="bform-field">
                                <label className="bform-label">Téléphone</label>
                                <input
                                    className="bform-input"
                                    type="tel"
                                    value={contactPhone}
                                    onChange={e => setContactPhone(e.target.value)}
                                    placeholder="+33 6 00 00 00 00"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Section Informations commerciales ─────────────────── */}
                <div className="bform-section">
                    <div className="bform-section-head">
                        <SectionIcon>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="1" x2="12" y2="23"/>
                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                            </svg>
                        </SectionIcon>
                        <span className="bform-section-title">Informations commerciales</span>
                    </div>
                    <div className="bform-section-body">
                        <div className="bform-row">
                            <div className="bform-field">
                                <label className="bform-label">Nombre de ventes réalisées</label>
                                <input
                                    className="bform-input"
                                    type="number"
                                    min="0"
                                    value={salesCount}
                                    onChange={e => setSalesCount(e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                            <div className="bform-field">
                                <label className="bform-label">
                                    Documents
                                    <span className="bform-optional">Optionnel</span>
                                </label>
                                <input
                                    className="bform-input"
                                    type="text"
                                    value={documents}
                                    onChange={e => setDocuments(e.target.value)}
                                    placeholder="URLs séparées par des virgules"
                                />
                                <span className="bform-hint">Lookbook, catalogue, conditions…</span>
                            </div>
                        </div>
                    </div>
                </div>

            </form>

            <div className="bform-footer">
                <button
                    className="bform-btn-primary"
                    form="bform-main"
                    type="submit"
                    disabled={saving || uploadingLogo || uploadingCover}
                >
                    {saving && <span className="bform-btn-save-spinner" />}
                    {saving ? 'Sauvegarde…' : 'Sauvegarder les informations'}
                </button>
            </div>
        </div>
    );
};

export default Form;
